import React from "react";
import {
  Box,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { LoyaltyPreset } from "../../../types/loyalty";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import {
  ValueRewardsDraft,
  RedemptionIncrement,
  EARN_PER_AMOUNT,
  normalizeRate,
  presetShowsProducts,
  presetShowsServices,
  redeemRewardName,
} from "./types";

interface ValueRewardsFormProps {
  value: ValueRewardsDraft;
  onChange: (value: ValueRewardsDraft) => void;
  /** Which earn inputs are visible: retail hides services, service hides
   *  products. */
  preset: LoyaltyPreset;
  /** Hide the step heading when embedded in a Settings edit modal. */
  showHeading?: boolean;
}

/** "$1.00 earns [ 5 |points]" — number input with a grey "points" suffix
 *  segment (reference design 2026-08-19). */
function RateRow({
  prefix,
  ariaLabel,
  value,
  onChange,
}: {
  prefix: string;
  ariaLabel: string;
  value: number | string;
  onChange: (value: number | string) => void;
}) {
  return (
    <Group gap="sm" wrap="nowrap">
      <Text>{prefix}</Text>
      <NumberInput
        aria-label={ariaLabel}
        value={value}
        onChange={(raw) => onChange(normalizeRate(raw))}
        min={1}
        clampBehavior="none"
        allowDecimal={false}
        allowNegative={false}
        hideControls
        w={200}
        rightSectionWidth={64}
        rightSectionPointerEvents="none"
        rightSection={
          <Box
            w="100%"
            h="100%"
            bg="gray.1"
            display="flex"
            style={{
              alignItems: "center",
              justifyContent: "center",
              borderLeft: "1px solid var(--mantine-color-gray-4)",
              borderRadius:
                "0 var(--mantine-radius-default) var(--mantine-radius-default) 0",
            }}
          >
            <Text size="sm" c="dimmed">
              points
            </Text>
          </Box>
        }
        styles={{ input: { textAlign: "right", paddingRight: 76 } }}
      />
    </Group>
  );
}

/** "$[X] equals [Y] points" — one redemption row (single, not repeatable). */
function IncrementRow({
  value,
  onChange,
}: {
  value: RedemptionIncrement;
  onChange: (row: RedemptionIncrement) => void;
}) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-end">
      <NumberInput
        aria-label="Cash value"
        value={value.cashValue}
        onChange={(raw) => {
          const next = typeof raw === "number" ? Math.max(0, raw) : raw;
          onChange({ ...value, cashValue: next });
        }}
        min={0}
        clampBehavior="none"
        allowNegative={false}
        decimalScale={2}
        prefix="$"
        hideControls
        w={140}
      />
      <Text pb={6}>equals</Text>
      <NumberInput
        aria-label="Points cost"
        value={value.pointsCost}
        onChange={(raw) =>
          onChange({ ...value, pointsCost: normalizeRate(raw) })
        }
        min={1}
        clampBehavior="none"
        allowDecimal={false}
        allowNegative={false}
        hideControls
        w={140}
      />
      <Text pb={6}>points</Text>
    </Group>
  );
}

/**
 * Step 2 — Transaction Based Rewards (loyalty FE1 §2.2, single-increment
 * reference design 2026-08-25 punch list). Earning Points: one rate per
 * applicable type (services / products). Redemption increment: one
 * {cash_value, points_cost} row; publish writes one spend rule per visible
 * type and ONE reward (auto-named).
 */
export default function ValueRewardsForm({
  value,
  onChange,
  preset,
  showHeading = true,
}: ValueRewardsFormProps) {
  const showServices = presetShowsServices(preset);
  const showProducts = presetShowsProducts(preset);
  const dollar = formatCurrency(EARN_PER_AMOUNT);

  const setIncrement = (row: RedemptionIncrement) =>
    onChange({ ...value, redemptionIncrement: row });

  return (
    <Stack gap="lg">
      {showHeading && (
        <Stack gap={4}>
          <Title order={2}>Transaction Based Rewards</Title>
          <Group gap={4} wrap="nowrap" align="center">
            <Text size="sm" c="dimmed">
              Decide the number of loyalty points earned for each dollar spent
              and set the redemption value.
            </Text>
            <Tooltip
              label="Points apply to services and products only — tips are not included."
              multiline
              w={280}
            >
              <ThemeIcon
                variant="transparent"
                color="var(--brand-teal)"
                size="sm"
              >
                <IconInfoCircle size={16} />
              </ThemeIcon>
            </Tooltip>
          </Group>
        </Stack>
      )}

      <Stack gap="md">
        <Title order={3}>Earning Points</Title>
        {showServices && (
          <Stack gap="xs">
            <Text fw={600} size="sm">
              How many points do services earn?
            </Text>
            <RateRow
              prefix={`${dollar} earns`}
              ariaLabel="How many points do services earn?"
              value={value.servicesRate}
              onChange={(servicesRate) => onChange({ ...value, servicesRate })}
            />
          </Stack>
        )}
        {showProducts && (
          <Stack gap="xs">
            <Text fw={600} size="sm">
              How many points do products earn?
            </Text>
            <RateRow
              prefix={`${dollar} earns`}
              ariaLabel="How many points do products earn?"
              value={value.productsRate}
              onChange={(productsRate) => onChange({ ...value, productsRate })}
            />
          </Stack>
        )}
      </Stack>

      <Divider />

      <Stack gap="md">
        <Stack gap={2}>
          <Title order={3}>Redemption value</Title>
          <Text size="sm" c="dimmed">
            Set the cash value clients get for their points.
          </Text>
        </Stack>
        <Stack gap={4}>
          <IncrementRow
            value={value.redemptionIncrement}
            onChange={setIncrement}
          />
          <Text size="xs" c="dimmed">
            {redeemRewardName(
              value.redemptionIncrement.pointsCost,
              value.redemptionIncrement.cashValue,
            )}
          </Text>
        </Stack>
      </Stack>
    </Stack>
  );
}
